import { Component } from "react"
import maplibregl from "maplibre-gl"
import "maplibre-gl/dist/maplibre-gl.css"
import Marker from "src/components/comparable_homes/Marker"
import StateConstants from "src/states/StateContstants"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import "src/components/maps/AddressMap.css"

const MARKER_ID_KEY = "data-marker-id"

function centerEqual(a: { lt: number | string; ln: number | string }, b: { lt: number | string; ln: number | string }): boolean {
    const latA = typeof a.lt === "string" ? parseFloat(a.lt) : a.lt
    const lnA = typeof a.ln === "string" ? parseFloat(a.ln) : a.ln
    const latB = typeof b.lt === "string" ? parseFloat(b.lt) : b.lt
    const lnB = typeof b.ln === "string" ? parseFloat(b.ln) : b.ln
    return latA === latB && lnA === lnB
}

function markersEqual(
    a: Array<{ id: string; lt: number | string; ln: number | string }> | undefined,
    b: Array<{ id: string; lt: number | string; ln: number | string }> | undefined
): boolean {
    if (a === b) return true
    if (!a || !b || a.length !== b.length) return false
    return a.every((m, i) => {
        const n = b[i]
        return n && m.id === n.id && centerEqual({ lt: m.lt, ln: m.ln }, { lt: n.lt, ln: n.ln })
    })
}

interface AreaMapProps {
    center: { lt: number | string; ln: number | string }
    markers?: Array<{ id: string; lt: number | string; ln: number | string; address?: string }>
    className?: string
    stateHandler?: EventAdaptingStateHandler
}

export default class AreaMap extends Component<AreaMapProps> {
    mapContainer: HTMLDivElement | null = null
    map: maplibregl.Map | null = null
    centreMarker: maplibregl.Marker | null = null
    saleMarkers: maplibregl.Marker[] = []
    initialBounds: maplibregl.LngLatBounds | null = null

    componentDidMount() {
        if (this.props.stateHandler) {
            this.props.stateHandler.addListener(StateConstants.MAP_MARKER_HOVER, (id: string | null) => {
                this.updateMarkerHighlight(id ?? null)
            })
        }
        setTimeout(() => this.initializeMap(), 0)
    }

    componentDidUpdate(prevProps: AreaMapProps) {
        const centerChanged = !centerEqual(prevProps.center, this.props.center)
        const markersChanged = !markersEqual(prevProps.markers, this.props.markers)
        if (centerChanged || markersChanged) {
            this.saleMarkers.forEach((m) => m.remove())
            this.saleMarkers = []
            if (this.centreMarker) this.centreMarker.remove()
            this.centreMarker = null
            if (this.map) {
                this.map.remove()
                this.map = null
            }
            setTimeout(() => this.initializeMap(), 0)
        }
    }

    componentWillUnmount() {
        this.saleMarkers.forEach((m) => m.remove())
        this.saleMarkers = []
        if (this.centreMarker) this.centreMarker.remove()
        if (this.map) this.map.remove()
        this.map = null
        this.centreMarker = null
    }

    initializeMap() {
        const { center, markers = [] } = this.props
        const lat = typeof center.lt === "string" ? parseFloat(center.lt) : center.lt
        const lng = typeof center.ln === "string" ? parseFloat(center.ln) : center.ln
        if (!this.mapContainer || isNaN(lat) || isNaN(lng)) return
        if (this.mapContainer.offsetWidth === 0 || this.mapContainer.offsetHeight === 0) {
            setTimeout(() => this.initializeMap(), 100)
            return
        }

        this.mapContainer.innerHTML = ""

        try {
            this.map = new maplibregl.Map({
                container: this.mapContainer,
                style: {
                    version: 8,
                    sources: {
                        osm: {
                            type: "raster",
                            tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
                            tileSize: 256,
                            attribution: "© OpenStreetMap contributors"
                        }
                    },
                    layers: [
                        {
                            id: "osm-tiles",
                            type: "raster",
                            source: "osm",
                            minzoom: 0,
                            maxzoom: 19
                        }
                    ]
                },
                center: [lng, lat],
                zoom: 14,
                attributionControl: { compact: false },
                cooperativeGestures: true
            })

            this.centreMarker = new maplibregl.Marker({
                element: Marker.createMarkerElement("", true, lat)
            })
                .setLngLat([lng, lat])
                .addTo(this.map)

            const bounds = new maplibregl.LngLatBounds()
            bounds.extend([lng, lat])

            markers.forEach((m) => {
                const mLat = typeof m.lt === "string" ? parseFloat(m.lt) : m.lt
                const mLng = typeof m.ln === "string" ? parseFloat(m.ln) : m.ln
                if (isNaN(mLat) || isNaN(mLng)) return
                const marker = new maplibregl.Marker({
                    element: Marker.createMarkerElement(m.id, false, mLat)
                })
                    .setLngLat([mLng, mLat])
                    .addTo(this.map!)
                marker.getElement().setAttribute(MARKER_ID_KEY, m.id)
                if (this.props.stateHandler) {
                    const el = marker.getElement()
                    const handleSelect = () => {
                        this.props.stateHandler!.set(StateConstants.MAP_MARKER_HOVER, m.id)
                        this.updateMarkerHighlight(m.id)
                    }
                    el.addEventListener("click", handleSelect)
                    el.addEventListener("touchstart", handleSelect, { passive: true })
                }
                this.saleMarkers.push(marker)
                bounds.extend([mLng, mLat])
            })

            if (this.props.stateHandler) {
                const currentId = this.props.stateHandler.get(StateConstants.MAP_MARKER_HOVER)
                this.updateMarkerHighlight(currentId ?? null)
            }

            if (this.map && !bounds.isEmpty()) {
                this.map.fitBounds(bounds, { padding: 50, maxZoom: 16 })
                this.initialBounds = bounds
            }

            setTimeout(() => this.map?.resize(), 100)
        } catch (error) {
            console.error("Error initializing area map:", error)
        }
    }

    updateMarkerHighlight(highlightedId: string | null) {
        this.saleMarkers.forEach((marker) => {
            const el = marker.getElement()
            const id = el.getAttribute(MARKER_ID_KEY)
            if (id === highlightedId) {
                el.classList.add("highlighted-marker")
            } else {
                el.classList.remove("highlighted-marker")
            }
        })
    }

    render() {
        return (
            <div className="property-map-container">
                <div
                    ref={(el) => (this.mapContainer = el)}
                    className={`map-wrapper ${this.props.className}`}
                >
                    <div className="map-loading">Map ...</div>
                </div>
                <div className="map-controls">
                    <button
                        className="map-control-btn"
                        aria-label="Zoom in"
                        onClick={() => this.map?.zoomIn()}
                    >+</button>
                    <button
                        className="map-control-btn"
                        aria-label="Zoom out"
                        onClick={() => this.map?.zoomOut()}
                    >−</button>
                    <button
                        className="map-control-btn map-control-home"
                        aria-label="Reset view"
                        onClick={() => {
                            if (this.map && this.initialBounds) {
                                this.map.fitBounds(this.initialBounds, { padding: 50, maxZoom: 16 })
                            }
                        }}
                    >⌂</button>
                </div>
            </div>
        )
    }
}
